import React from "react";

import cx from "classnames";

import { coalesce } from "@kazoohr/helpers";

import { Button } from "../Button";
import { Icon, IconDictionary } from "../Icon";
import { IconButton } from "../IconButton";
import { Popover } from "../Popover";

import styles from "./MeatballMenu.scss";

export type MeatballProps = {
  elements: MeatballElement[];
  className?: string;
  label?: string;
  isDisabled?: boolean;
  ["data-testid"]?: string;
};

export type MeatballElement = {
  href?: string;
  onClick?: () => void;
  text: React.ReactNode;
  title: string;
  icon?: IconDictionary;
  disabled?: boolean;
  openInNewTab?: boolean;
  linkElement?: React.ElementType;
};

export function MeatballMenu({
  elements,
  className,
  label,
  isDisabled,
  ["data-testid"]: testId,
}: MeatballProps) {
  const [isPopOverOpen, setIsPopOverOpen] = React.useState(false);
  const PopOverClassName = cx(className, styles.popoverContainer, {
    [styles["icon-popoverContainer"]]: !label,
  });

  const onElementClick = (onClick: MeatballElement["onClick"]) => {
    setIsPopOverOpen(false);
    // istanbul ignore else - noop
    if (onClick && typeof onClick === "function") {
      onClick();
    }
  };

  const containerTestId = `${coalesce(testId, "meatBallMenu")}-button`;

  // isDisabled should prevent interaction, but should not change the appearance of the buttons
  // So we should not use the disabled property on Button
  const buttonComponent = label ? (
    <Button
      onClick={() => !isDisabled && setIsPopOverOpen(!isPopOverOpen)}
      data-testid={containerTestId}
      variant="secondary"
      size="extra_small"
      icon="caret_dn"
      iconAlignment="right"
      aria-disabled={isDisabled}
      type="button"
    >
      {label}
    </Button>
  ) : (
    <IconButton
      icon="meatball_menu"
      iconSize={24}
      className={styles["meatball--button"]}
      onClick={() => !isDisabled && setIsPopOverOpen(!isPopOverOpen)}
      data-testid={containerTestId}
      aria-haspopup="true"
      aria-expanded={isPopOverOpen}
      aria-disabled={isDisabled}
      type="button"
    />
  );

  const LinksList = (items: MeatballElement[]) => {
    return (
      <ul
        className={styles.unorderedList}
        data-testid="meatBallMenu-list"
        role="menu"
        title="hello"
      >
        {items.map((item, index) => {
          const TagName = getElementTag(item);
          const openInNewTabProps =
            item.href && item.openInNewTab
              ? {
                  target: "_blank",
                  rel: "noreferrer noopener",
                }
              : null;
          return (
            <li
              key={`meatBallMenu-element-${index}`}
              className={styles.listElement}
              role="none"
            >
              <TagName
                data-testid={`tag-${item.title}`}
                href={item.href}
                onClick={() => onElementClick(item.onClick)}
                title={item.title}
                className={styles.listChildElement}
                role="menuitem"
                disabled={item.disabled}
                {...openInNewTabProps}
              >
                {item.icon && (
                  <Icon
                    className={styles.icon}
                    color="#2a5cdb"
                    icon={
                      // Can be removed when we convert the icon component to TSX
                      // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
                      item.icon as any
                    }
                    variant="thin"
                  />
                )}
                {item.text}
              </TagName>
            </li>
          );
        })}
      </ul>
    );
  };

  return (
    <Popover
      isOpen={isPopOverOpen}
      onSetOpen={
        // istanbul ignore next - simple closure
        (value: boolean) => setIsPopOverOpen(value)
      }
      parent={buttonComponent}
      popoverComponent={LinksList(elements)}
      className={PopOverClassName}
      position="bottom-end"
      data-testid={testId}
    />
  );
}

function getElementTag(item: MeatballElement): React.ElementType | string {
  if (item?.linkElement) {
    return item.linkElement;
  }
  if (item?.href) {
    return "a";
  }
  return "button";
}
